/* eslint-disable @typescript-eslint/no-non-null-assertion */
import "./Location.css";

import React from "react";

import { LocationPlaceholderIcon } from "../../../../../assets";
// eslint-disable-next-line unused-imports/no-unused-imports
import { Column, ErrorBox, MapBox, MapControl, MapLayerOptions, MapOpenLayer, Row } from "../../../../../widget";
import { UploadLocationDocumentModal } from "./components/UploadDocumentModal";
import { useLocation } from "./useLocation";

const Location = (): JSX.Element => {
  const {
    locationData,
    showMenuToggle,
    selectedLayer,
    gisFile,
    is3d,
    setIs3d,
    changeSelectedLayer,
    isToDrawBoundary,
    setIsToDrawBoundary,
    isToUploadManually,
    setIsToUploadManually,
    uploadProjectLocationFile,
    isToSaveBoundary,
    setIsToSaveBoundary,
    clearDrawBoundaryToggle,
    cleanShape,
    errors,
    drawShapeJson,
    setDrawShapeJson,
    inputSelectedDrawOption,
    setInputSelectedDrawOption,
  } = useLocation();

  return (
    <div className="ProjectTabSection">
      <Row spacingV="2xl">
        <Column span={12}>
          <h2>Location</h2>
        </Column>
      </Row>
      {errors && errors.length > 0 && (
        <Row spacingV="ll">
          <Column span={12}>
            <ErrorBox text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}
      {locationData.latitude && locationData.longitude ? (
        <>
          {showMenuToggle && (
            <Row spacingV="2xl" justify="between">
              <Column span={12}>
                <span>
                  Use the mapping tool to explore your project location in detail. Toggle data layers from within the
                  drop-down menu to gain insights into the site’s environmental conditions or progress. Note: only the
                  base layer is available in the 3D terrain view.
                </span>
              </Column>
            </Row>
          )}
          <Row>
            <Column span={12}>
              <div className="map-container">
                <MapControl
                  is3d={is3d}
                  setIs3d={setIs3d}
                  showMenuToggle={showMenuToggle}
                  onChange3dView={(e1) => {
                    setIs3d(e1);
                    if (e1) {
                      changeSelectedLayer(MapLayerOptions.BASE);
                    }
                  }}
                  onChangeLayer={(e2) => {
                    changeSelectedLayer(e2 as MapLayerOptions);
                  }}
                  disableSelection={selectedLayer === MapLayerOptions.BASE && is3d}
                  selectedLayer={selectedLayer}
                  changeSelectedLayer={changeSelectedLayer}
                  cleanShape={cleanShape}
                  setIsToDrawBoundary={setIsToDrawBoundary}
                  setIsToUploadManually={setIsToUploadManually}
                  setIsToSaveBoundary={setIsToSaveBoundary}
                  inputSelectedDrawOption={inputSelectedDrawOption}
                  setInputSelectedDrawOption={setInputSelectedDrawOption}
                />
                {is3d === true ? (
                  <MapBox
                    projectShape={{
                      shapeFileUrl: gisFile.projectShape?.geoJsonFileUrl || "",
                      fillColor: "#FF865D",
                    }}
                    coordinate={{ lat: Number(locationData.latitude!), lng: Number(locationData.longitude!) }}
                    is3d={is3d}
                  />
                ) : (
                  <MapOpenLayer
                    coordinate={{ lat: Number(locationData.latitude!), lng: Number(locationData.longitude!) }}
                    gisFile={gisFile}
                    drawLayer={isToDrawBoundary}
                    exportBoundary={setDrawShapeJson}
                    clearDrawBoundaryToggle={clearDrawBoundaryToggle}
                  />
                )}
              </div>
            </Column>
          </Row>
          <UploadLocationDocumentModal
            show={isToUploadManually || isToSaveBoundary}
            closeModal={() => {
              setDrawShapeJson("");
              setIsToUploadManually(false);
              setIsToSaveBoundary(false);
              setInputSelectedDrawOption("");
            }}
            saveBoundaryFile={(file, visibility, selectedOrganisations) => {
              uploadProjectLocationFile(file, visibility, selectedOrganisations);
              setIsToUploadManually(false);
              setIsToSaveBoundary(false);
              setInputSelectedDrawOption("");
            }}
            drawShapeJson={drawShapeJson}
          />
        </>
      ) : (
        <>
          <Row spacingV="ml" justify="center">
            <Column span={12}>
              <LocationPlaceholderIcon />
            </Column>
          </Row>
          <Row spacingV="ml" justify="center">
            <Column span={12}>
              <h1 className="LocationEmptyStateHeader">Create your project boundary</h1>
            </Column>
          </Row>
          <Row spacingV="2xl" justify="center">
            <Column span={6}>
              <p className="body2 NoData">
                Add project coordinates in the ‘Overview’ tab and then draw or upload a shape file to plot your
                boundaries on the map and bring your project to life
              </p>
            </Column>
          </Row>
        </>
      )}
    </div>
  );
};

export const LocationTab = React.memo(Location);
